import React, { useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import Modal from "Components/Generic/Modal";
import { Transshipment } from "Models/transshipment";
import { TransshipmentAssigneeComponent } from "../components/TransshipmentAssigneeComponent";
import { useTransshipmentActions } from "../hooks/useActions";
import { useStyles } from "./styles";

interface IChangeTransshipmentAssigneeModal {
  isOpen: boolean;
  onCloseModal: () => void;
  transshipment?: Transshipment;
  refetchTransshipment?: () => void;
}

export const ChangeTransshipmentAssigneeModal = ({
  isOpen,
  onCloseModal,
  transshipment,
  refetchTransshipment,
}: IChangeTransshipmentAssigneeModal) => {
  const {
    getTransshipmentsAssigneeLeader,
    updateTransshipmentList,
    invalidateTransshipmentsCache,
    isUpdatingTransshipmentAssigneeLeader,
  } = useTransshipmentActions();
  const { data: leaders, isFetching: isLeadersListLoading } = getTransshipmentsAssigneeLeader();
  const { t: translate } = useTranslation();
  const classes = useStyles();

  const [newTransshipmentLeaderId, setNewTransshipmentLeaderId] = useState<number | null>(
    transshipment?.leaderId ?? null
  );

  useEffect(() => {
    setNewTransshipmentLeaderId(transshipment?.leaderId ?? null);
  }, [transshipment]);

  const isSameLeaderAssigned = useMemo(() => {
    if (newTransshipmentLeaderId) {
      if (transshipment?.leaderId === null) {
        return true;
      }

      if (transshipment?.leaderId !== newTransshipmentLeaderId) {
        return true;
      }
    } else if (transshipment?.leaderId && newTransshipmentLeaderId === null) {
      return true;
    }

    return false;
  }, [transshipment?.leaderId, newTransshipmentLeaderId]);

  const handleCloseModal = () => {
    onCloseModal();
    setNewTransshipmentLeaderId(null);
  };

  const handleSubmitChangeAssigneeLeader = () => {
    if (transshipment && transshipment?.id) {
      updateTransshipmentList(newTransshipmentLeaderId, transshipment.id, () => {
        invalidateTransshipmentsCache();
        if (refetchTransshipment) refetchTransshipment?.();
        handleCloseModal();
      });
    }
  };

  return (
    <Modal
      open={isOpen}
      title={`${translate("changeAssignee")}${
        transshipment?.reference ? ` ${translate("for")} ${transshipment?.reference}` : ""
      }`}
      className={classes.modal}
      onClose={handleCloseModal}
      handleClose={handleCloseModal}
      showActionButtons
      actionButtonProps={{
        onCancel: handleCloseModal,
        textCancel: translate("cancel"),
        onSave: handleSubmitChangeAssigneeLeader,
        textSave: translate("save"),
        saveDisabled: !isSameLeaderAssigned,
        classesSave: classes.saveButton,
        classesCancel: classes.cancelButton,
        loading: isUpdatingTransshipmentAssigneeLeader,
      }}
    >
      <TransshipmentAssigneeComponent
        transshipment={transshipment}
        handleNewTransshipmentLeaderId={(leaderId) => setNewTransshipmentLeaderId(leaderId)}
        leaders={leaders}
        isLeadersLoading={isLeadersListLoading}
      />
    </Modal>
  );
};
